/**
 * Utility for generating comic panel layouts
 */

// Generate a random color for panel visualization
const getRandomColor = (index) => {
    const hues = [0, 30, 60, 120, 180, 210, 270, 300];
    const hue = hues[index % hues.length];
    return `hsl(${hue}, 70%, 85%)`;
};

/**
 * Generate layout options based on dimensions and panel count
 * @param {number} width - Page width
 * @param {number} height - Page height
 * @param {number} count - Number of panels desired
 * @returns {Array} List of layout objects
 */
export const generateLayouts = (width, height, count) => {
    const layouts = [];
    const gap = 20; // Default gap between panels

    // Layout 1: Equal Grid
    layouts.push({
        id: `grid-${count}`,
        name: "Equal Grid",
        panels: generateGrid(width, height, count, gap)
    });

    // Layout 2: Smart Split (Variant A)
    // Only generate if count > 1
    if (count > 1) {
        layouts.push({
            id: `split-a-${count}`,
            name: "Dynamic Layout A",
            panels: generateRecursiveSplit(width, height, count, gap, 0)
        });
    }


    // Layout 3: Smart Split (Variant B)
    if (count > 2) {
        layouts.push({
            id: `split-b-${count}`,
            name: "Dynamic Layout B",
            panels: generateRecursiveSplit(width, height, count, gap, 1) // Different seed
        });
    }

    // Layout 4: Cinematic Splash (New)
    // Always available as it's a cool effect
    layouts.push({
        id: `splash-${count}`,
        name: "Cinematic Splash",
        panels: generateCinematicSplash(width, height, count, gap)
    });

    return layouts;
};

// Helper: Generate structured grid
const generateGrid = (width, height, count, gap) => {
    let cols = 1;
    let rows = 1;

    // Simple logic to find best grid fit
    if (count === 2) { rows = 2; }
    else if (count === 3) { rows = 3; } // Vertical strip usually better for comics
    else if (count === 4) { cols = 2; rows = 2; }
    else if (count <= 6) { cols = 2; rows = 3; }
    else if (count <= 9) { cols = 3; rows = 3; }
    else { cols = 3; rows = Math.ceil(count / 3); }

    // Adjust for landscape pages - favor cols over rows
    if (width > height) {
        [cols, rows] = [rows, cols];
    }

    const panels = [];
    const panelW = (width - (gap * (cols + 1))) / cols;
    const panelH = (height - (gap * (rows + 1))) / rows;

    for (let i = 0; i < count; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);

        // Check if valid grid position (might have empty slots if count < rows*cols)
        if (row < rows) {
            panels.push({
                id: `p-${i + 1}`,
                x: gap + (col * (panelW + gap)),
                y: gap + (row * (panelH + gap)),
                width: panelW,
                height: panelH,
                color: getRandomColor(i)
            });
        }
    }

    // Center the last row if uneven? implementation detail
    return panels;
};

// Helper: Recursive split algorithm for "Mondrian-style" panels
const generateRecursiveSplit = (width, height, targetCount, gap, seedOffset = 0) => {
    // Start with one full page panel
    let panels = [{
        x: gap,
        y: gap,
        width: width - (gap * 2),
        height: height - (gap * 2)
    }];

    // Helper for deterministic pseudo-randomness
    const random = (idx) => {
        const x = Math.sin(idx + seedOffset) * 10000;
        return x - Math.floor(x);
    };

    let splitIndex = 0;

    // Recursively split random panels until we reach target count
    while (panels.length < targetCount) {
        // Find largest panel to split
        let largestIdx = 0;
        let maxArea = 0;

        panels.forEach((p, idx) => {
            const area = p.width * p.height;
            if (area > maxArea) {
                maxArea = area;
                largestIdx = idx;
            }
        });

        const target = panels[largestIdx];

        // Decide split direction based on aspect ratio
        // If very wide, split vertically. If very tall, split horizontally.
        const splitVertically = target.width > target.height * 1.2;
        const splitHorizontally = target.height > target.width * 1.2;

        // If roughly square, choose randomly based on seed
        const isVertical = splitVertically || (!splitHorizontally && random(splitIndex) > 0.5);

        // Remove the target panel
        panels.splice(largestIdx, 1);

        // Add two new panels
        if (isVertical) {
            const w1 = (target.width - gap) * (0.4 + (random(splitIndex + 100) * 0.2)); // Split between 40-60%
            const w2 = target.width - w1 - gap;

            panels.push(
                { x: target.x, y: target.y, width: w1, height: target.height },
                { x: target.x + w1 + gap, y: target.y, width: w2, height: target.height }
            );
        } else {
            const h1 = (target.height - gap) * (0.4 + (random(splitIndex + 100) * 0.2));
            const h2 = target.height - h1 - gap;

            panels.push(
                { x: target.x, y: target.y, width: target.width, height: h1 },
                { x: target.x, y: target.y + h1 + gap, width: target.width, height: h2 }
            );
        }

        splitIndex++;
    }

    // Assign IDs and colors
    return panels.map((p, i) => ({
        ...p,
        id: `p-${i + 1}`,
        color: getRandomColor(i)
    }));
};

// Helper: Cinematic Splash (Background + Insets)
const generateCinematicSplash = (width, height, count, gap) => {
    const panels = [];

    // 1. Background Panel (Always Panel 1)
    panels.push({
        id: 'p-1',
        x: 0,
        y: 0,
        width: width,
        height: height,
        color: 'hsl(0, 0%, 95%)', // Light gray background
        isBackground: true,
        zIndex: 0
    });

    if (count < 2) return panels;

    // 2. Inset Panels
    const insetW = width * 0.35; // 35% width for insets
    const insetH = height * 0.25;

    // Position strategies

    // 2 Panels: Bottom Right Corner Inset
    if (count === 2) {
        panels.push({
            id: 'p-2',
            x: width - insetW - gap,
            y: height - insetH - gap,
            width: insetW,
            height: insetH,
            color: getRandomColor(1),
            zIndex: 10
        });
    }
    // 3 Panels: Insets on right side
    else if (count === 3) {
        panels.push({
            id: 'p-2',
            x: width - insetW - gap,
            y: gap * 2,
            width: insetW,
            height: insetH,
            color: getRandomColor(1),
            zIndex: 10
        });
        panels.push({
            id: 'p-3',
            x: width - insetW - gap,
            y: height - insetH - (gap * 2),
            width: insetW,
            height: insetH,
            color: getRandomColor(2),
            zIndex: 10
        });
    }
    // 4+ Panels: Vertical strip on the right
    else {
        const colW = width * 0.25;
        // Remaining Panels
        const insetCount = count - 1;
        const totalGap = gap * (insetCount + 1);
        const colH = (height - totalGap) / insetCount;

        for (let i = 1; i < count; i++) {
            panels.push({
                id: `p-${i + 1}`,
                x: width - colW - gap,
                y: gap + ((i - 1) * (colH + gap)),
                width: colW,
                height: colH,
                color: getRandomColor(i),
                zIndex: 10
            });
        }
    }

    return panels;
};
